import React, { useState } from "react";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Panel, Field } from "./ui";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

/**
 * "Delete my account" — the member's own erasure (DSGVO Art. 17).
 *
 * Two steps on purpose: the panel only explains, and the form asks for the password
 * and a current authenticator code, which the server checks again
 * (routes_auth.delete_account). What is deleted and what is kept is the same list the
 * privacy policy gives, because both come from erasure.erase_user.
 */
export default function DeleteAccount() {
  const { t } = useTranslation();
  const { isRealAdmin, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.deleteAccount({ password, code });
      localStorage.removeItem("costock_user");
      toast.success(t("deleteAccount.done"));
      // The session cookies are already gone; a full reload leaves nothing in memory.
      setTimeout(() => { window.location.href = "/"; }, 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dialogs.failed"));
      setBusy(false);
    }
  };

  return (
    <Panel className="card p-5 sm:p-6 border-rose-200" testid="delete-account-panel">
      <div className="flex items-center gap-2 mb-2">
        <Trash2 size={18} className="text-rose-500" />
        <h2 className="font-heading font-semibold tracking-tight text-lg">{t("deleteAccount.title")}</h2>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">{t("deleteAccount.body")}</p>
      <p className="text-sm text-slate-500 leading-relaxed mt-2">{t("deleteAccount.kept")}</p>

      {isRealAdmin || user?.impersonated ? (
        <p className="mt-4 text-sm text-slate-500" data-testid="delete-account-unavailable">
          {t(user?.impersonated ? "deleteAccount.notInSupport" : "deleteAccount.adminNote")}
        </p>
      ) : !open ? (
        <button className="btn-outline mt-4 text-rose-600 border-rose-200 hover:bg-rose-50"
                onClick={() => setOpen(true)} data-testid="delete-account-start">
          <Trash2 size={16} /> {t("deleteAccount.start")}
        </button>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="delete-account-form">
          <div className="flex gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg p-3">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <p>{t("deleteAccount.exportFirst")}</p>
          </div>
          <Field label={t("auth.password")}>
            <input type="password" className="input" autoComplete="current-password" required
                   value={password} onChange={(e) => setPassword(e.target.value)}
                   data-testid="delete-account-password" />
          </Field>
          <Field label={t("deleteAccount.code")}>
            <input className="input tabular tracking-[0.3em]" inputMode="numeric" autoComplete="one-time-code"
                   placeholder="000000" required value={code}
                   onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                   data-testid="delete-account-code" />
          </Field>
          <label className="flex items-start gap-2.5 text-sm cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={sure} onChange={(e) => setSure(e.target.checked)}
                   data-testid="delete-account-confirm" />
            <span className="text-slate-700">{t("deleteAccount.confirm")}</span>
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <button type="submit" disabled={busy || !sure || !password || code.length !== 6}
                    className="btn-primary bg-rose-600 hover:bg-rose-700 justify-center"
                    data-testid="delete-account-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              {t("deleteAccount.submit")}
            </button>
            <button type="button" className="btn-ghost justify-center" disabled={busy}
                    onClick={() => { setOpen(false); setPassword(""); setCode(""); setSure(false); }}>
              {t("common.cancel")}
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}
